import {AllActivityTypes, AllActivityTypesArray} from "../../types-enums/activity";
import {createContext} from "react";
import {JourneyOverviewDayCardProps} from "./journey-overview/journey-day-card/JourneyDayCard";
import {UniqueIdentifier} from "@dnd-kit/core";
import groupBy from "lodash/groupBy";
import {get} from "lodash";
import {DurationProps} from "../../types-enums/activity-common-properties";

export interface JourneyDayCardProps {
    draggingActivity: AllActivityTypes | null,
    day: number,
    droppableZoneId: string,
    isRecalculatingActivityEmissions: boolean,
    allActivities: AllActivityTypesArray,
    onActivityArrowUp?: (activity: AllActivityTypes) => void
    onActivityDelete: (activity: AllActivityTypes) => void
    onActivityArrowDown: (activity: AllActivityTypes) => void,
    journeyDurationInDays: JourneyDnDCommonProps["journeyDurationInDays"],
}

export interface JourneyDnDCommonProps {
    draggingActivity: AllActivityTypes | null,
    hasExactDates: boolean,
    isRecalculatingActivityEmissions: boolean,
    journeyDurationInDays?: number,
    onDeleteActivity: (activity: AllActivityTypes, dropbableZoneId?: JourneyDayCardProps["droppableZoneId"]) => void,
    onActivityArrow: (
        droppableZoneId: JourneyDayCardProps["droppableZoneId"],
        whichArrow: 'up' | 'down',
        activity: AllActivityTypes,
        index?: number
    ) => void
    activities: AllActivityTypesArray,
    isOverAllActivitiesDroppableZone?: boolean,
}

export interface CommonContextProps {
    day?: number,
    droppableZoneId: JourneyDayCardProps['droppableZoneId'],
    isRecalculatingActivityEmissions: boolean,
    hasExactDates: boolean,
    activitiesAtDay: AllActivityTypesArray,
    allActivities: AllActivityTypesArray,
    onActivityArrowUp?: (activity: AllActivityTypes) => void
    onActivityDelete: (activity: AllActivityTypes) => void
    onActivityArrowDown: (activity: AllActivityTypes) => void,
    journeyDurationInDays?: JourneyDnDCommonProps["journeyDurationInDays"],
    draggingActivity?: AllActivityTypes | null,
}

export interface CreateJourneyFromTravelComponentsDayCardPropsContextProps extends CommonContextProps {
}

export const CreateJourneyFromTravelComponentsDayCardPropsContext = createContext<CreateJourneyFromTravelComponentsDayCardPropsContextProps>({
    day: 1,
    journeyDurationInDays: 1,
    droppableZoneId: "",
    isRecalculatingActivityEmissions: false,
    activitiesAtDay: [],
    allActivities: [],
    onActivityArrowUp: () => {
    },
    onActivityDelete: () => {
    },
    onActivityArrowDown: () => {
    },
    draggingActivity: null,
    hasExactDates: false,
})

export interface JourneyDayCardContextProps extends CommonContextProps {
    isRecalculatingActivityEmissions: boolean,
    onDeleteDay: (day: number) => void,
    altKeyPressed: boolean,
    onMouseEnter: JourneyOverviewDayCardProps["onMouseEnter"],
    onMouseLeave: JourneyOverviewDayCardProps["onMouseLeave"],
    hoveredId: JourneyOverviewDayCardProps["hoveredId"],
    travelComponentActivities?: AllActivityTypesArray,
    journeyDuration: DurationProps,
    journeyPersonCount: number,
}

export const JourneyDayCardContext = createContext<JourneyDayCardContextProps>({
    journeyDuration: {} as DurationProps,
    day: 1,
    journeyDurationInDays: 0,
    journeyPersonCount: 0,
    droppableZoneId: "",
    activitiesAtDay: [],
    onActivityArrowUp: () => {
    },
    onActivityDelete: () => {
    },
    onActivityArrowDown: () => {
    },
    draggingActivity: null,
    isRecalculatingActivityEmissions: false,
    onDeleteDay: () => {
    },
    onMouseEnter: () => {
    },
    onMouseLeave: () => {
    },
    hoveredId: "",
    altKeyPressed: false,
    hasExactDates: false,
    allActivities: [],
})

/**
 * A function to determine if a given value represents a valid day.
 *
 * Checks whether the provided value can be successfully converted into a valid number,
 * which may represent a day. The value is considered valid if it is not NaN when converted
 * to a numeric type.
 *
 * @param {string | number} value - The input value to validate, which can be either a string or a number.
 * @returns {boolean} - Returns true if the value represents a valid day, otherwise false.
 */
export const isValidDay = (value: string | number): boolean => !isNaN(Number(value))

/**
 * Converts a unique identifier to a number.
 *
 * This function takes a unique identifier, which is expected to be a value
 * that can be converted to a string, and parses it into a base-10 integer.
 *
 * @param {UniqueIdentifier} value - The unique identifier that will be converted to a number.
 *                                   It should be a type that can be converted to a string.
 * @returns {number} The numeric representation of the provided unique identifier.
 */
export const convertUniqueIdentifierToNumber = (value: UniqueIdentifier): number => parseInt(value.toString(), 10)

/**
 * Groups activities based on their travel option type.
 *
 * This function takes an array of activities, evaluates the 'common.travelOptionType'
 * property of each activity, and organizes them into groups where all activities
 * in a group share the same travel option type. The output is an object where the
 * keys represent distinct travel option types, and each key maps to an array of
 * activities that correspond to that particular travel option type.
 *
 * @param {AllActivityTypesArray} activities - Array of activities to be grouped.
 * @returns {Object} - An object containing grouped activities, keyed by travel option type.
 */
export const groupActivitiesByTravelOptionType = (activities: AllActivityTypesArray) => groupBy(activities, activity => get(activity, 'common.travelOptionType'))

export const COMMON_COLOR = "white"